import Code from '@/components/dashboard/Code';
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import { useRef } from 'react'

export default function TADoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Technical Analysis Indicators" description="A comprehensive collection of technical analysis indicators for financial data analysis.
          These functions calculate various market indicators used in technical trading strategies."  canonicalUrl="https://axionquant.com/docs/client-libraries/python-SDK/technical-analysis" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Technical Analysis Indicators</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          A comprehensive collection of technical analysis indicators for financial data analysis.
          These functions calculate various market indicators used in technical trading strategies.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Rate of Change (ROC) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Rate of Change (ROC)</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  roc(df, column="close", period=10)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates the Rate of Change percentage over a specified period.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">10</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for ROC calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with ROC percentage values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                ROC = ((current_price / price_n_periods_ago) - 1) * 100
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ROC Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">ROC Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period ROC
roc_series = ta.roc(df, column='close', period=5)
print(roc_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0          NaN
1          NaN
2          NaN
3          NaN
4          NaN
5     4.000000
6     3.921569
7     3.883495
8     1.923077
9     3.809524
Name: roc, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Momentum (MOM) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Momentum</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  mom(df, column="close", period=10)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates the difference between current price and price n periods ago.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">10</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for momentum calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with momentum values (price difference).
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                Momentum = current_price - price_n_periods_ago
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Momentum Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Momentum Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 104, 103, 105, 107, 106, 108, 110, 109]
})

# Calculate 5-period momentum
momentum_series = ta.mom(df, column='close', period=5)
print(momentum_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0    NaN
1    NaN
2    NaN
3    NaN
4    NaN
5    7.0
6    4.0
7    5.0
8    5.0
9    2.0
Name: mom, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Simple Moving Average (SMA) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Simple Moving Average</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  sma(df, column="close", period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates the Simple Moving Average over a specified period.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for SMA calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Simple Moving Average values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                SMA = (price₁ + price₂ + ... + priceₙ) / n
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="SMA Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">SMA Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period SMA
sma_series = ta.sma(df, column='close', period=5)
print(sma_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0      NaN
1      NaN
2      NaN
3      NaN
4    102.2
5    103.0
6    103.8
7    105.2
8    106.0
9    106.8
Name: sma, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Simple Moving Median (SMM) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Simple Moving Median</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  smm(df, column="close", period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates the median value over a specified rolling window.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for median calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with moving median values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                SMM = median(price₁, price₂, ..., priceₙ)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="SMM Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">SMM Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period SMM
smm_series = ta.smm(df, column='close', period=5)
print(smm_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0      NaN
1      NaN
2      NaN
3      NaN
4    102.0
5    103.0
6    104.0
7    105.0
8    106.0
9    107.0
Name: smm, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Smoothed Simple Moving Average (SSMA) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Smoothed Simple Moving Average</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  ssma(df, column="close", period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates a smoothed version of the Simple Moving Average using exponential smoothing.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for SSMA calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with smoothed moving average values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                SSMAₜ = EMA(SMA, α=1/period)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="SSMA Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">SSMA Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period SSMA
ssma_series = ta.ssma(df, column='close', period=5)
print(ssma_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0          NaN
1          NaN
2          NaN
3          NaN
4    102.200000
5    102.533333
6    103.355556
7    104.570370
8    105.046914
9    106.031276
Name: ssma, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Exponential Moving Average (EMA) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Exponential Moving Average</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  ema(df, column="close", period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates the Exponential Moving Average which gives more weight to recent prices.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for EMA calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Exponential Moving Average values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                EMAₜ = (Priceₜ × k) + (EMAₜ-₁ × (1 - k))<br/>
                where k = 2/(period + 1)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="EMA Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">EMA Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period EMA
ema_series = ta.ema(df, column='close', period=5)
print(ema_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0          NaN
1          NaN
2          NaN
3          NaN
4    102.200000
5    102.800000
6    103.866667
7    105.244444
8    105.829630
9    106.886420
Name: ema, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Double Exponential Moving Average (DEMA) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Double Exponential Moving Average</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  dema(df, column="close", period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates the Double EMA, which applies EMA smoothing twice for reduced lag.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for DEMA calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Double Exponential Moving Average values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                DEMA = 2 × EMA - EMA(EMA)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="DEMA Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">DEMA Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period DEMA
dema_series = ta.dema(df, column='close', period=5)
print(dema_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0          NaN
1          NaN
2          NaN
3          NaN
4    102.200000
5    103.137778
6    105.029630
7    107.159012
8    107.264198
9    109.159538
Name: dema, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Triangular Moving Average (TRIMA) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Triangular Moving Average</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  trima(df, column="close", period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates a double-smoothed moving average for reduced noise.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for TRIMA calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Triangular Moving Average values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                TRIMA = SMA(SMA(price, period), period)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="TRIMA Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">TRIMA Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period TRIMA
trima_series = ta.trima(df, column='close', period=5)
print(trima_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0      NaN
1      NaN
2      NaN
3      NaN
4      NaN
5      NaN
6      NaN
7      NaN
8    104.2
9    104.8
Name: trima, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Average True Range (ATR) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Average True Range</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  atr(df, period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Measures market volatility by calculating the average of true ranges over a period.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for ATR calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Average True Range values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">True Range Calculation</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                TR = max(high - low, |high - prev_close|, |low - prev_close|)<br/>
                ATR = SMA(TR, period)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ATR Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">ATR Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLC data
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110],
    'low': [99, 101, 100, 102, 104],
    'close': [102, 104, 103, 105, 107]
})

# Calculate 3-period ATR
atr_series = ta.atr(df, period=3)
print(atr_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0         NaN
1         NaN
2    4.666667
3    4.222222
4    4.148148
Name: atr, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Stochastic Oscillator */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Stochastic Oscillator</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  stochastic_oscillator(df, k_period=14, d_period=3)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates %K and %D stochastic oscillator values to identify overbought/oversold conditions.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">k_period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for %K calculation</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">d_period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">3</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Smoothing period for %D</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns two pandas Series: %K (fast stochastic) and %D (slow stochastic).
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                %K = 100 × ((close - lowest_low) / (highest_high - lowest_low))<br/>
                %D = SMA(%K, d_period)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Stochastic Oscillator Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Stochastic Oscillator Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLC data
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110, 109, 111],
    'low': [99, 101, 100, 102, 104, 103, 105],
    'close': [102, 104, 103, 105, 107, 106, 108]
})

# Calculate stochastic oscillator
stoch_k, stoch_d = ta.stochastic_oscillator(df, k_period=5, d_period=3)
print("Stochastic %K:", stoch_k)
print("Stochastic %D:", stoch_d)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`Stochastic %K: 0         NaN
1         NaN
2         NaN
3         NaN
4    50.000000
5    66.666667
6    57.142857
Name: stoch_k, dtype: float64

Stochastic %D: 0         NaN
1         NaN
2         NaN
3         NaN
4         NaN
5         NaN
6    57.936508
Name: stoch_d, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Chande Momentum Oscillator (CMO) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Chande Momentum Oscillator</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  cmo(df, column="close", period=20)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Measures momentum by comparing sum of gains to sum of losses over a period.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">20</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for CMO calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with CMO values ranging from -100 to +100.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                CMO = 100 × ((sum_gains - sum_losses) / (sum_gains + sum_losses))
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="CMO Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">CMO Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period CMO
cmo_series = ta.cmo(df, column='close', period=5)
print(cmo_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0         NaN
1         NaN
2         NaN
3         NaN
4         NaN
5   60.000000
6   33.333333
7   60.000000
8   20.000000
9   60.000000
Name: cmo, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* On Balance Volume (OBV) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">On Balance Volume</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  obv(df)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Cumulatively adds or subtracts volume based on price direction to measure buying/selling pressure.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'close' and 'volume' columns</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with cumulative On Balance Volume values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                OBVₜ = OBVₜ-₁ + <br/>
                &nbsp;&nbsp;volumeₜ if closeₜ {`>`} closeₜ-₁<br/>
                &nbsp;&nbsp;-volumeₜ if closeₜ {`>`}  closeₜ-<br/>
                &nbsp;&nbsp;0 if closeₜ = closeₜ-₁
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="OBV Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">OBV Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLCV data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105],
    'volume': [1000, 1500, 1200, 1800, 2000]
})

# Calculate OBV
obv_series = ta.obv(df)
print(obv_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0    1000
1    2500
2    1300
3    3100
4    5100
Name: obv, dtype: int64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Volume Price Trend (VPT) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Volume Price Trend</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  vpt(df)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Measures the relationship between volume and price changes by cumulating volume adjusted by price percentage changes.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'close' and 'volume' columns</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with cumulative Volume Price Trend values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                VPTₜ = VPTₜ-₁ + (volumeₜ × ((closeₜ - closeₜ-₁) / closeₜ-₁))
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="VPT Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">VPT Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105],
    'volume': [1000, 1500, 1200, 1800, 2000]
})

# Calculate VPT
vpt_series = ta.vpt(df)
print(vpt_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0       0.000000
1      30.000000
2      18.235294
3      53.184466
4      91.962275
Name: vpt, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Volume-Weighted Average Price (VWAP) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Volume-Weighted Average Price</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  vwap(df, period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates the average price weighted by volume over a specified period.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close', 'volume' columns</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Rolling period for VWAP calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Volume-Weighted Average Price values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                VWAP = (sum(volume × typical_price)) / (sum(volume))<br/>
                typical_price = (high + low + close) / 3
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="VWAP Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">VWAP Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLCV data
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110],
    'low': [99, 101, 100, 102, 104],
    'close': [102, 104, 103, 105, 107],
    'volume': [1000, 1500, 1200, 1800, 2000]
})

# Calculate VWAP
vwap_series = ta.vwap(df, period=3)
print(vwap_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0           NaN
1           NaN
2    103.175676
3    104.557377
4    105.595745
Name: vwap, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Bollinger Bands */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Bollinger Bands</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  bbands(df, column="close", period=20, num_std_dev=2)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates upper and lower Bollinger Bands around a simple moving average.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">20</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for SMA calculation</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">num_std_dev</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int/float</td>
                      <td className="px-4 py-3 text-sm text-gray-700">2</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Number of standard deviations for bands</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns three pandas Series: upper_band, middle_band (SMA), lower_band.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                middle_band = SMA(close, period)<br/>
                upper_band = middle_band + (std_dev × num_std_dev)<br/>
                lower_band = middle_band - (std_dev × num_std_dev)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Bollinger Bands Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Bollinger Bands Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate Bollinger Bands
upper, middle, lower = ta.bbands(df, period=5, num_std_dev=2)
print("Upper Band:", upper)
print("Middle Band:", middle)
print("Lower Band:", lower)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`Upper Band: 4        106.549742
5        107.303261
6        109.063901
7        111.133918
8        111.207841
9        112.456119
Name: bbands_upper, dtype: float64

Middle Band: 4    102.2
5    103.0
6    103.8
7    105.2
8    106.0
9    106.8
Name: bbands_middle, dtype: float64

Lower Band: 4     97.850258
5     98.696739
6     98.536099
7     99.266082
8    100.792159
9    101.143881
Name: bbands_lower, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Keltner Channels */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Keltner Channels</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  kc(df, period=20, atr_period=10, multiplier=2)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates volatility-based channels using ATR around an exponential moving average.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">20</td>
                      <td className="px-4 py-3 text-sm text-gray-700">EMA period for middle line</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">atr_period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">10</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Period for ATR calculation</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">multiplier</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int/float</td>
                      <td className="px-4 py-3 text-sm text-gray-700">2</td>
                      <td className="px-4 py-3 text-sm text-gray-700">ATR multiplier for channel width</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns three pandas Series: upper_channel, middle_line, lower_channel.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                middle_line = EMA(close, period)<br/>
                upper_channel = middle_line + (ATR × multiplier)<br/>
                lower_channel = middle_line - (ATR × multiplier)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Keltner Channels Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Keltner Channels Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLC data
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110],
    'low': [99, 101, 100, 102, 104],
    'close': [102, 104, 103, 105, 107]
})

# Calculate Keltner Channels
upper, middle, lower = ta.kc(df, period=3, atr_period=3, multiplier=2)
print("Upper Channel:", upper)
print("Middle Line:", middle)
print("Lower Channel:", lower)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`Upper Channel: 0           NaN
1           NaN
2    109.888889
3    110.666667
4    112.296296
Name: kc_upper, dtype: float64

Middle Line: 0           NaN
1           NaN
2    103.000000
3    104.000000
4    105.666667
Name: kc_middle, dtype: float64

Lower Channel: 0           NaN
1           NaN
2     96.111111
3     97.333333
4     99.037037
Name: kc_lower, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Kaufman's Adaptive Moving Average (KAMA) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Kaufman's Adaptive Moving Average</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  kama(df, column="close", period=14, fast=14, slow=30)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Adaptive moving average that adjusts its sensitivity based on market volatility.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Efficiency ratio period</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">fast</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Fast EMA constant period</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">slow</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">30</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Slow EMA constant period</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Kaufman's Adaptive Moving Average values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                ER = direction / volatility<br/>
                SC = [ER × (fastest - slowest) + slowest]²<br/>
                KAMAₜ = KAMAₜ-₁ + SC × (price - KAMAₜ-₁)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="KAMA Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">KAMA Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate KAMA
kama_series = ta.kama(df, column='close', period=5)
print(kama_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0          NaN
1          NaN
2          NaN
3          NaN
4    102.200000
5    103.103252
6    104.383699
7    106.149310
8    106.431297
9    107.774876
Name: kama, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Vortex Indicator (VI) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Vortex Indicator</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  vi(df, period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Measures trend direction and strength using positive and negative vortex movements.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for VI calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns two pandas Series: VI_plus (positive vortex) and VI_minus (negative vortex).
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Vortex Indicator Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Vortex Indicator Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLC data
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110, 109, 111],
    'low': [99, 101, 100, 102, 104, 103, 105],
    'close': [102, 104, 103, 105, 107, 106, 108]
})

# Calculate Vortex Indicator
vi_plus, vi_minus = ta.vi(df, period=5)
print("VI+:", vi_plus)
print("VI-:", vi_minus)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`VI+: 0         NaN
1         NaN
2         NaN
3         NaN
4    1.245192
5    1.183790
6    1.193062
Name: vi_plus, dtype: float64

VI-: 0         NaN
1         NaN
2         NaN
3         NaN
4    0.832453
5    0.822703
6    0.844121
Name: vi_minus, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Moving Average Convergence Divergence (MACD) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Moving Average Convergence Divergence</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  macd(df, column="close", fast_period=12, slow_period=26, signal_period=9)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates MACD line, signal line, and histogram for trend-following momentum indicator.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">fast_period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">12</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Fast EMA period</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">slow_period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">26</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Slow EMA period</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">signal_period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">9</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Signal line EMA period</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns three pandas Series: macd_line, signal_line, histogram.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                MACD = EMA(fast_period) - EMA(slow_period)<br/>
                Signal = EMA(MACD, signal_period)<br/>
                Histogram = MACD - Signal
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="MACD Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">MACD Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate MACD
macd_line, signal_line, histogram = ta.macd(df, fast_period=5, slow_period=8, signal_period=3)
print("MACD Line:", macd_line)
print("Signal Line:", signal_line)
print("Histogram:", histogram)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`MACD Line: 0         NaN
1         NaN
2         NaN
3         NaN
4         NaN
5         NaN
6         NaN
7    0.881433
8    0.495703
9    1.079230
Name: macd, dtype: float64

Signal Line: 0         NaN
1         NaN
2         NaN
3         NaN
4         NaN
5         NaN
6         NaN
7    0.881433
8    0.688568
9    0.883899
Name: signal, dtype: float64

Histogram: 0         NaN
1         NaN
2         NaN
3         NaN
4         NaN
5         NaN
6         NaN
7    0.000000
8   -0.192865
9    0.195331
Name: histogram, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Williams %R */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Williams %R</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  williams_r(df, period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Momentum indicator measuring overbought/oversold levels, similar to stochastic oscillator.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for Williams %R</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Williams %R values ranging from -100 to 0.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                Williams %R = -100 × ((highest_high - close) / (highest_high - lowest_low))
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Williams %R Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Williams %R Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLC data
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110],
    'low': [99, 101, 100, 102, 104],
    'close': [102, 104, 103, 105, 107]
})

# Calculate Williams %R
williams_r_series = ta.williams_r(df, period=5)
print(williams_r_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0          NaN
1          NaN
2          NaN
3          NaN
4   -50.000000
Name: williams_r, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Average Directional Index (ADX) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Average Directional Index</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  adx(df, period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Measures trend strength without regard to direction.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for ADX calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Average Directional Index values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                +DI = 100 × SMA(+DM) / ATR<br/>
                -DI = 100 × SMA(-DM) / ATR<br/>
                DX = 100 × |+DI - -DI| / (+DI + -DI)<br/>
                ADX = SMA(DX, period)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ADX Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">ADX Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLC data
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110, 109, 111],
    'low': [99, 101, 100, 102, 104, 103, 105],
    'close': [102, 104, 103, 105, 107, 106, 108]
})

# Calculate ADX
adx_series = ta.adx(df, period=5)
print(adx_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0          NaN
1          NaN
2          NaN
3          NaN
4          NaN
5          NaN
6          NaN
7          NaN
8    32.495897
Name: adx, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Relative Strength Index (RSI) */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Relative Strength Index</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  rsi(df, column="close", period=14)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Measures speed and change of price movements to identify overbought/oversold conditions.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Pandas DataFrame containing price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">column</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm text-gray-700">"close"</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Column name for price data</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">period</td>
                      <td className="px-4 py-3 text-sm text-gray-700">int</td>
                      <td className="px-4 py-3 text-sm text-gray-700">14</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Lookback period for RSI calculation</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with RSI values ranging from 0 to 100.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                RSI = 100 - (100 / (1 + RS))<br/>
                RS = average_gain / average_loss
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="RSI Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">RSI Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample data
df = pd.DataFrame({
    'close': [100, 102, 101, 103, 105, 104, 106, 108, 107, 109]
})

# Calculate 5-period RSI
rsi_series = ta.rsi(df, column='close', period=5)
print(rsi_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0          NaN
1          NaN
2          NaN
3          NaN
4          NaN
5    61.904762
6    55.555556
7    70.000000
8    57.142857
9    70.000000
Name: rsi, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Ichimoku Cloud */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Ichimoku Cloud</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  ichi(df)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Comprehensive indicator that provides support/resistance, trend direction, and momentum.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns five pandas Series: conversion_line, base_line, leading_span_a, leading_span_b, lagging_span.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                Conversion Line = (9-period high + 9-period low) / 2<br/>
                Base Line = (26-period high + 26-period low) / 2<br/>
                Leading Span A = (Conversion Line + Base Line) / 2 (shifted forward 26 periods)<br/>
                Leading Span B = (52-period high + 52-period low) / 2 (shifted forward 26 periods)<br/>
                Lagging Span = Close (shifted back 26 periods)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Ichimoku Cloud Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Ichimoku Cloud Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLC data (typically requires more data points)
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110, 109, 111, 112, 113, 114],
    'low': [99, 101, 100, 102, 104, 103, 105, 106, 107, 108],
    'close': [102, 104, 103, 105, 107, 106, 108, 109, 110, 111]
})

# Calculate Ichimoku Cloud
conversion, base, span_a, span_b, lag = ta.ichi(df)
print("Conversion Line:", conversion)
print("Base Line:", base)
print("Leading Span A:", span_a)
print("Leading Span B:", span_b)
print("Lagging Span:", lag)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`Conversion Line: 0         NaN
1         NaN
2         NaN
3         NaN
4         NaN
5         NaN
6         NaN
7         NaN
8    107.5
9    108.5
Name: conversion_line, dtype: float64

Base Line: 0         NaN
1         NaN
2         NaN
3         NaN
4         NaN
5         NaN
6         NaN
7         NaN
8    108.0
9    108.5
Name: base_line, dtype: float64
...`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Parabolic SAR */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Parabolic SAR</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  sar(df, af=0.02, af_max=0.2)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Trend-following indicator that provides potential reversal points.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">af</td>
                      <td className="px-4 py-3 text-sm text-gray-700">float</td>
                      <td className="px-4 py-3 text-sm text-gray-700">0.02</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Acceleration factor increment</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">af_max</td>
                      <td className="px-4 py-3 text-sm text-gray-700">float</td>
                      <td className="px-4 py-3 text-sm text-gray-700">0.2</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Maximum acceleration factor</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns a pandas Series with Parabolic SAR values.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formula</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                SARₜ = SARₜ-₁ + AF × (EP - SARₜ-₁)<br/>
                where EP is the extreme point (highest high in uptrend, lowest low in downtrend)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Parabolic SAR Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Parabolic SAR Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd

# Sample OHLC data
df = pd.DataFrame({
    'high': [105, 107, 106, 108, 110, 109, 111],
    'low': [99, 101, 100, 102, 104, 103, 105],
    'close': [102, 104, 103, 105, 107, 106, 108]
})

# Calculate Parabolic SAR
sar_series = ta.sar(df, af=0.02, af_max=0.2)
print(sar_series)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`0     99.000000
1     99.000000
2     99.000000
3     99.000000
4     99.000000
5     99.000000
6    104.060000
Name: sar, dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Fibonacci Pivot Points */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">Fibonacci Pivot Points</h2>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  FUNCTION
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  fib(df)
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Calculates Fibonacci-based support and resistance levels using previous period's high, low, and close.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Default</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">df</td>
                      <td className="px-4 py-3 text-sm text-gray-700">DataFrame</td>
                      <td className="px-4 py-3 text-sm text-gray-700">-</td>
                      <td className="px-4 py-3 text-sm text-gray-700">Must contain 'high', 'low', 'close' columns</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Returns</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  Returns seven pandas Series: PP, R1, S1, R2, S2, R3, S3.
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Formulas</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm">
                PP = (high + low + close) / 3<br/>
                R1 = PP + 0.382 × (high - low)<br/>
                S1 = PP - 0.382 × (high - low)<br/>
                R2 = PP + 0.618 × (high - low)<br/>
                S2 = PP - 0.618 × (high - low)<br/>
                R3 = PP + 1.000 × (high - low)<br/>
                S3 = PP - 1.000 × (high - low)
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Fibonacci Pivot Points Example" contentRef={sectionRef} />
              <h3 className="text-sm font-semibold mb-3">Fibonacci Pivot Points Example</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Python</p>
                <Code language="python">{`from axion import ta
import pandas as pd
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

# Sample OHLC data
df = pd.DataFrame({
    'high': [110, 108, 107],
    'low': [100, 102, 101],
    'close': [105, 106, 104]
})

# Calculate Fibonacci Pivot Points
PP, R1, S1, R2, S2, R3, S3 = ta.fib(df)
print("PP:", PP)
print("R1:", R1)
print("S1:", S1)
print("R2:", R2)
print("S2:", S2)
print("R3:", R3)
print("S3:", S3)`}</Code>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Output</p>
                <Code>{`PP: 0    105.0
1    105.0
2    104.0
dtype: float64
R1: 0    108.82
1    107.29
2    106.29
dtype: float64
S1: 0    101.18
1    102.71
2    101.71
dtype: float64
R2: 0    111.18
1    108.71
2    107.71
dtype: float64
S2: 0     98.82
1    101.29
2    100.29
dtype: float64
R3: 0    115.0
1    111.0
2    110.0
dtype: float64
S3: 0     95.0
1     99.0
2     98.0
dtype: float64`}</Code>
              </div>
            </div>
          </div>
        </div>

        {/* Data Requirements */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Data Requirements</h2>
            <p className="text-gray-700 mb-6">
              Most functions require specific columns in the DataFrame. Below are the common requirements:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Basic Price Data</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">close</code> - Closing price</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">high</code> - Highest price</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">low</code> - Lowest price</li>
                <li><code className="text-gray-800 bg-gray-100 px-2 py-1 rounded">volume</code> - Trading volume</li>
              </ul>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Indicator Categories</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li><strong>Trend Indicators:</strong> SMA, EMA, MACD, ADX</li>
                <li><strong>Momentum Indicators:</strong> RSI, Stochastic, Williams %R</li>
                <li><strong>Volatility Indicators:</strong> Bollinger Bands, ATR, Keltner Channels</li>
                <li><strong>Volume Indicators:</strong> OBV, VPT, VWAP</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
